import { useDelayedLoading } from '../lib/useDelayedLoading';

/**
 * LoadingIndicator — renders nothing for the first 300ms, then shows
 * "Loading…" (or whatever label you pass) if the parent is still in
 * the loading state.
 *
 * Why: most Supabase queries on a decent connection return inside
 * 200ms. Showing a "Loading…" string for that brief moment flashes
 * 1990s-style on every page transition. By delaying the indicator,
 * fast loads (the vast majority) render the AppShell chrome instantly,
 * keep the content area momentarily empty, then drop in the real
 * content — perceived as instant. Slow loads (over the threshold)
 * still get a visible indicator so the user knows the app's working.
 *
 * Drop-in replacement for `<p className="muted">Loading…</p>`. Use
 * everywhere we currently have that pattern.
 */
export function LoadingIndicator({
  delayMs = 1200,
  label = 'Loading…',
}: {
  delayMs?: number;
  label?: string;
}) {
  // The hook returns false during the delay window, then true. Since
  // this component is only rendered while the parent is "loading",
  // we don't need a separate isLoading prop — being mounted is the
  // signal.
  const show = useDelayedLoading(true, delayMs);
  if (!show) return null;
  return <p className="muted" aria-live="polite">{label}</p>;
}
